import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieConsent } from "@/components/CookieConsent";
import { MobileBottomNav } from "@/components/MobileBottomNav";

// Only eagerly load the homepage for fast initial paint
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Events = lazy(() => import("./pages/Events"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const EditEvent = lazy(() => import("./pages/EditEvent"));
const Experiences = lazy(() => import("./pages/Experiences"));
const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const AddExperience = lazy(() => import("./pages/AddExperience"));
const EditExperience = lazy(() => import("./pages/EditExperience"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ForOrganizers = lazy(() => import("./pages/ForOrganizers"));
const ForExperienceOwners = lazy(() => import("./pages/ForExperienceOwners"));
const ForDrivers = lazy(() => import("./pages/ForDrivers"));
const VehicleCheck = lazy(() => import("./pages/VehicleCheck"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MapView = lazy(() => import("./pages/MapView"));
const Recommendations = lazy(() => import("./pages/Recommendations"));
const Press = lazy(() => import("./pages/Press"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ActivityFeed = lazy(() => import("./pages/ActivityFeed"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const SearchPage = lazy(() => import("./pages/Search"));

const App = () => (
  <TooltipProvider>
    <ScrollToTop />
    <Toaster />
    <Sonner />
    <CookieConsent />
    <MobileBottomNav />
    <div className="pb-24 md:pb-0">
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/event/:slug" element={<EventDetail />} />
          <Route path="/events" element={<Events />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/event/:slug/edit" element={<EditEvent />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/my-events" element={<Dashboard />} />
          <Route path="/my-experiences" element={<Dashboard />} />
          <Route path="/favorites" element={<Dashboard />} />
          <Route path="/profile" element={<Dashboard />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/experiences" element={<Experiences />} />
          <Route path="/experiences/new" element={<AddExperience />} />
          <Route path="/experiences/:slug" element={<ExperienceDetail />} />
          <Route path="/experiences/:slug/edit" element={<EditExperience />} />
          <Route path="/map" element={<MapView />} />
          
          <Route path="/recommendations" element={<Recommendations />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/owner-dashboard" element={<OwnerDashboard />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/for-organizers" element={<ForOrganizers />} />
          <Route path="/for-experience-owners" element={<ForExperienceOwners />} />
          <Route path="/for-drivers" element={<ForDrivers />} />
          <Route path="/vehicle-check" element={<VehicleCheck />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/press" element={<Press />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/activity" element={<ActivityFeed />} />
          <Route path="/user/:userId" element={<UserProfile />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/location/:slug" element={<LocationPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  </TooltipProvider>
);

export default App;
